import React from 'react';
import { ArrowBackIcon, ArrowNextIcon, Greyscale } from 'glints-aries';
import { range } from 'lodash';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';

import { useConfig } from 'src/common/hooks';

import * as S from './AnchorPagination.sc';

const dots = '...';
const maxPaginationItems = 7;

const getPageNumbers = (currentPage: number, totalPage: number) => {
  if (totalPage <= maxPaginationItems) {
    return range(1, totalPage + 1);
  }

  if (currentPage <= 3) {
    return [...range(1, 5 + 1), dots, totalPage];
  }

  if (currentPage > 3 && currentPage < totalPage - 3) {
    return [
      1,
      dots,
      ...range(currentPage - 1, currentPage + 1 + 1),
      dots,
      totalPage,
    ];
  }

  return [1, dots, ...range(totalPage - 4, totalPage + 1)];
};

interface Props {
  currentPage?: number;
  totalPage: number;
  basePathname?: string;
  isInternalSearchExplorePage?: boolean;
}

/**
 * this component is using Anchor tag to change page,
 * currently it's only made for the Seo module,
 * we will need to extend it to make it accept dynamic routing parameters
 * and preserve other query parameters
 */
export const AnchorPagination: React.FC<React.PropsWithChildren<Props>> = ({
  currentPage = 1,
  totalPage,
  basePathname,
  isInternalSearchExplorePage = false,
}) => {
  const config = useConfig();
  const pathPrefix = config.BASENAME;
  const router = useRouter();
  const query = router.query;
  const { filter } = query;
  const pageNumbers = getPageNumbers(currentPage, totalPage);
  const shouldRenderBackButton = currentPage > 1;
  const shouldRenderNextButton = currentPage < totalPage;

  /**
   *  isInitialInternalSearchPage means the intial url when user first open
   *  this QueryExplorePage from Google.
   *  Example:  /explore/food-safety/in-dki-jakarta
   */
  const isInitialInternalSearchPage = filter && isInternalSearchExplorePage;

  const getHref = (page: number) => {
    const pageNumber = page as any as string;

    if (basePathname) {
      if (!isInitialInternalSearchPage) {
        return getHrefWithQuery(query, page);
      } else if (page === 1) {
        return `${basePathname}`;
      } else return `${basePathname}?page=${pageNumber}`;
    }

    return getHrefWithQuery(query, page);
  };

  const getHrefWithQuery = (query: ParsedUrlQuery, page: number): string => {
    const { page: _, ...restQuery } = query;

    const selectedQuery = {
      ...restQuery,
      ...(page === 1 ? {} : { page }),
    };

    return `${pathPrefix === '/' ? '' : pathPrefix}${
      router.pathname
    }?${new URLSearchParams(selectedQuery as any).toString()}`;
  };

  return (
    <>
      <Head>
        <link rel="next" href={getHref(currentPage + 1)} />
        {shouldRenderBackButton && (
          <link rel="prev" href={getHref(currentPage - 1)} />
        )}
      </Head>
      {!isNaN(totalPage) && totalPage > 1 && (
        <S.PaginationContainer>
          {shouldRenderBackButton && (
            <a href={getHref(currentPage - 1)} aria-label="Previous Page">
              <S.Arrow aria-label="Previous Page">
                <ArrowBackIcon color={Greyscale.black} />
              </S.Arrow>
            </a>
          )}
          {pageNumbers.map((pageNumber) => {
            if (pageNumber === dots) {
              return <S.Dots key={dots}>{dots}</S.Dots>;
            }
            if (currentPage === pageNumber) {
              return (
                <S.Number key={pageNumber} isActive={true}>
                  {pageNumber}
                </S.Number>
              );
            }
            return (
              <a key={pageNumber} href={getHref(pageNumber as number)}>
                <S.Number isActive={false}>{pageNumber}</S.Number>
              </a>
            );
          })}
          {shouldRenderNextButton && (
            <a href={getHref(currentPage + 1)} aria-label="Next Page">
              <S.Arrow aria-label="Next Page">
                <ArrowNextIcon color={Greyscale.black} />
              </S.Arrow>
            </a>
          )}
        </S.PaginationContainer>
      )}
    </>
  );
};
